// FILE: frontend/src/components/kyb/sections/OfficersDisplay.tsx
// PURPOSE: Display company officers with proper data extraction

import React from 'react';
import { User, Calendar, Globe, Briefcase, ExternalLink, MapPin } from 'lucide-react';
import type { Officer } from '../../../types/kyb.types';

interface OfficersDisplayProps {
  officers?: Officer[];
  companyNumber?: string;
}

export const OfficersDisplay: React.FC<OfficersDisplayProps> = ({ 
  officers, 
  companyNumber 
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getRoleColor = (role?: string, isResigned?: boolean) => {
    if (isResigned) return 'bg-red-100 text-red-800 border-red-200';
    
    const roleUpper = role?.toUpperCase() || '';
    if (roleUpper.includes('DIRECTOR')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (roleUpper.includes('SECRETARY')) return 'bg-purple-100 text-purple-800 border-purple-200';
    if (roleUpper.includes('MEMBER')) return 'bg-green-100 text-green-800 border-green-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Separate active and resigned officers using officer_status from Companies House
  const activeOfficers = officers?.filter(officer => officer.officer_status !== 'resigned') || [];
  const resignedOfficers = officers?.filter(officer => officer.officer_status === 'resigned') || [];

  if (!officers || officers.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-12 text-center">
        <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">No officer information available</p>
        <p className="text-sm text-slate-400 mt-2">Officer data may still be loading or unavailable for this company</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6 text-white" />
            <h3 className="text-xl font-bold text-white">Company Officers</h3>
          </div>
          <div className="flex gap-2">
            <span className="px-3 py-1 bg-white/20 text-white text-sm font-bold rounded-full">
              {activeOfficers.length} Active
            </span>
            {resignedOfficers.length > 0 && (
              <span className="px-3 py-1 bg-red-500/80 text-white text-sm font-bold rounded-full">
                {resignedOfficers.length} Resigned
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Officers Grid */}
      <div className="p-6">
        {/* Active Officers */}
        {activeOfficers.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Active Officers</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeOfficers.map((officer, index) => (
            <div 
              key={index}
              className="border border-slate-200 rounded-xl p-4 hover:shadow-lg transition-shadow duration-200 bg-slate-50"
            >
              {/* Officer Name */}
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <User className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-slate-900">
                    {officer.name || 'Unknown Officer'}
                  </h4>
                  {officer.role && (
                    <span className={`inline-block mt-1 px-2 py-1 text-xs font-semibold rounded-full border ${getRoleColor(officer.role, false)}`}>
                      {officer.role}
                    </span>
                  )}
                </div>
              </div>

              {/* Officer Details */}
              <div className="space-y-2">
                {officer.appointed_on && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Appointed:</span>
                    <span className="font-medium text-slate-900">
                      {formatDate(officer.appointed_on)}
                    </span>
                  </div>
                )}
                
                {officer.nationality && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Nationality:</span>
                    <span className="font-medium text-slate-900">
                      {officer.nationality}
                    </span>
                  </div>
                )}

                {officer.occupation && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Occupation:</span>
                    <span className="font-medium text-slate-900">
                      {officer.occupation}
                    </span>
                  </div>
                )}

                {officer.correspondence_address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-slate-600 block">Address:</span>
                      <span className="font-medium text-slate-900 text-xs leading-relaxed">
                        {officer.correspondence_address}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
          </div>
        )}

        {/* Resigned Officers */}
        {resignedOfficers.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Resigned Officers</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resignedOfficers.map((officer, index) => (
            <div 
              key={index}
              className="border border-red-200 rounded-xl p-4 hover:shadow-lg transition-shadow duration-200 bg-red-50"
            >
              {/* Officer Name */}
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <User className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-slate-900">
                    {officer.name || 'Unknown Officer'}
                  </h4>
                  {officer.role && (
                    <span className={`inline-block mt-1 px-2 py-1 text-xs font-semibold rounded-full border ${getRoleColor(officer.role, true)}`}>
                      {officer.role} - RESIGNED
                    </span>
                  )}
                </div>
              </div>

              {/* Officer Details */}
              <div className="space-y-2">
                {officer.appointed_on && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Appointed:</span>
                    <span className="font-medium text-slate-900">
                      {formatDate(officer.appointed_on)}
                    </span>
                  </div>
                )}

                {officer.resigned_on && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-red-500" />
                    <span className="text-red-600 font-semibold">Resigned:</span>
                    <span className="font-bold text-red-900">
                      {formatDate(officer.resigned_on)}
                    </span>
                  </div>
                )}
                
                {officer.nationality && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Nationality:</span>
                    <span className="font-medium text-slate-900">
                      {officer.nationality}
                    </span>
                  </div>
                )}

                {officer.occupation && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Occupation:</span>
                    <span className="font-medium text-slate-900">
                      {officer.occupation}
                    </span>
                  </div>
                )}

                {officer.correspondence_address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-slate-600 block">Address:</span>
                      <span className="font-medium text-slate-900 text-xs leading-relaxed">
                        {officer.correspondence_address}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
          </div>
        )}

        {/* View on Companies House Link */}
        {companyNumber && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <a
              href={`https://find-and-update.company-information.service.gov.uk/company/${companyNumber}/officers`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
            >
              View all officers on Companies House
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficersDisplay;